// Pure search helpers for the klantenservice index.
//
// Split out of HelpSearch.tsx so they can be tested without a DOM or a
// next-intl navigation context: importing the component pulls in
// @/i18n/navigation, which pulls in next/navigation, which does not resolve
// under vitest. Same reason the platform keeps flowStack.ts beside its
// component rather than inside it.

// Only the fields search and the result list need. The article body is
// deliberately absent: the full corpus is around 300KB and this data crosses
// into a client component, so carrying bodies would ship every article to
// every visitor of the index.
export type SearchableArticle = {
  slug: string;
  title: string;
  excerpt: string;
  readingMinutes: number;
};

export type SearchableCollection = {
  slug: string;
  title: string;
  description: string | null;
  articles: SearchableArticle[];
};

/**
 * Lowercase and strip diacritics, so "cafe" matches "café" either way round.
 *
 * The combining-mark range is written as escapes rather than as literal
 * characters: a bare combining range in source is invisible in most editors
 * and does not survive a careless copy-paste.
 */
export function normaliseForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Substring match over titles and excerpts.
 *
 * Deliberately not the platform's hybrid fulltext-plus-LIKE search. That one
 * queries the whole corpus for the widget; this filters 36 rows already in
 * memory, and a second relevance model for 36 rows would be two behaviours to
 * keep in agreement for no gain.
 */
export function filterArticles(
  collections: SearchableCollection[],
  query: string,
): SearchableArticle[] {
  const needle = normaliseForSearch(query.trim());
  const all = collections.flatMap((collection) => collection.articles);

  if (needle === "") return all;

  return all.filter((article) =>
    normaliseForSearch(`${article.title} ${article.excerpt}`).includes(needle),
  );
}
